/**
 * Settings → Your team.
 *
 * The screen a firm with more than one person needs, and the reason the rest of the team work
 * exists. Deliberately ONE screen rather than a step in sign-up: this is the same page on day one
 * and in March when somebody joins, so a wizard version would only ever have covered the first day
 * and this one would still have had to be built.
 *
 * Most firms are one person and stay that way. A sole practitioner should be able to read this in a
 * glance, see that it does not apply to them, and never think about it again — which is why there is
 * no empty state urging them to build a team they do not have.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { confirmAction } from '../../components/ui/notices.tsx';
import {
    inviteColleague,
    listFirmMembers,
    removeFirmMember,
    transferOwnership,
    updateFirmMember,
    type FirmMember,
} from '../../services/tenantApi.ts';

interface FirmTeamSettingsProps {
    tenantId: string;
    /** The signed-in user, so the screen can tell "you" from everyone else. */
    myUserId: string;
    /** Only the principal manages the team; everyone else reads it. */
    isPrincipal: boolean;
    /**
     * What this firm calls someone who holds clients. An accounting firm reads "Accountant" where an
     * advice firm reads "Adviser" — a role name is the most-repeated copy in a product, and calling
     * an accountant an adviser quietly contradicts the terms they signed.
     */
    holderLabel?: string;
}

const card = {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 16,
    boxShadow: '0 1px 3px rgba(15,23,42,.06)',
    marginTop: 14,
    overflow: 'hidden',
} as const;

const label = { fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#64748b', display: 'block', marginBottom: 6 } as const;
const input = { width: '100%', border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 11px', fontSize: 14, color: '#0f172a', background: '#fff' } as const;
const primaryBtn = { background: '#1a6fd4', color: '#fff', border: 0, borderRadius: 8, padding: '9px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' } as const;
const ghostBtn = { background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 13px', fontSize: 13, fontWeight: 600, cursor: 'pointer' } as const;

/** What this person's allowance means in a sentence, rather than four numbers to interpret. */
function planLine(member: FirmMember): string {
    if (!member.plan) return 'Holds no clients — free';
    const { mode, used, remaining } = member.plan;
    if (mode === 'comp') return 'Complimentary access — unlimited clients';
    if (mode === 'none') {
        // The moment that matters on this screen: an invited person who cannot start work yet.
        return 'No plan yet — cannot take on a client until one is chosen';
    }
    const plan = mode === 'annual' ? 'Annual plan' : 'Pay as you go';
    const left = remaining === null ? 'unlimited' : `${remaining} left`;
    return `${plan} · ${used} ${used === 1 ? 'client' : 'clients'} taken on · ${left}`;
}

function roleLine(member: FirmMember, holderLabel: string): string {
    if (member.role === 'owner') return `Principal · ${holderLabel.toLowerCase()} · sees every client`;
    if (member.role === 'adviser') return `${holderLabel} · sees their own clients`;
    return 'Support · holds no clients · sees the practice';
}

export function FirmTeamSettings({ tenantId, myUserId, isPrincipal, holderLabel = 'Adviser' }: FirmTeamSettingsProps) {
    const [members, setMembers] = useState<FirmMember[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState<'adviser' | 'staff'>('adviser');
    const [inviteLink, setInviteLink] = useState<string | null>(null);

    const [editingNumber, setEditingNumber] = useState<string | null>(null);
    const [numberDraft, setNumberDraft] = useState('');

    const load = useCallback(async () => {
        try {
            setMembers(await listFirmMembers(tenantId));
        } catch {
            setError('Could not load your team.');
        }
    }, [tenantId]);

    useEffect(() => {
        void load();
    }, [load]);

    const holders = useMemo(() => (members ?? []).filter((m) => m.role === 'owner' || m.role === 'adviser'), [members]);

    /** Run an action, surface whatever the server said, and re-read the roster either way. */
    const run = async (fn: () => Promise<{ ok: boolean; message?: string }>) => {
        setBusy(true);
        setError(null);
        const res = await fn();
        if (!res.ok) setError(res.message ?? 'That did not work.');
        await load();
        setBusy(false);
        return res.ok;
    };

    const send = async () => {
        const email = inviteEmail.trim();
        if (!email) return;
        setBusy(true);
        setError(null);
        const res = await inviteColleague(tenantId, email, inviteRole);
        if (!res.ok) {
            setError(res.message);
        } else {
            // Colleague invites are not emailed by the server — only client invites are — so this
            // link is the only way in. Showing it beats an adviser waiting for a message that was
            // never going to arrive.
            setInviteLink(`${window.location.origin}/?invite=${res.token}`);
            setInviteEmail('');
        }
        await load();
        setBusy(false);
    };

    if (!members) {
        return (
            <div style={card}>
                <div style={{ padding: 20, fontSize: 13, color: '#64748b' }}>Loading your team…</div>
            </div>
        );
    }

    return (
        <div style={card}>
            <div style={{ padding: '18px 20px 4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>Your team</div>
                    <div style={{ fontSize: 12.5, color: '#64748b' }}>
                        {holders.length} {holders.length === 1 ? holderLabel.toLowerCase() : `${holderLabel.toLowerCase()}s`}
                        {members.length - holders.length > 0 ? ` · ${members.length - holders.length} support` : ''}
                    </div>
                </div>
                <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.55, margin: '4px 0 0' }}>
                    {holderLabel}s hold their own clients and need their own plan. Support helps with anyone&rsquo;s
                    clients, holds none, and is free.
                </p>
            </div>

            {error && (
                <div style={{ margin: '12px 20px 0', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 10, padding: '10px 13px', fontSize: 13, lineHeight: 1.5 }}>
                    {error}
                </div>
            )}

            <div style={{ padding: '6px 20px 4px' }}>
                {members
                    .filter((m) => m.role !== 'client')
                    .map((m) => {
                        const isMe = m.userId === myUserId;
                        const holdsClients = m.role === 'owner' || m.role === 'adviser';
                        return (
                            <div key={m.userId} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '13px 0', borderTop: '1px solid #f1f5f9' }}>
                                <div style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, background: holdsClients ? 'linear-gradient(135deg,#1a6fd4,#17b8e0)' : '#f1f5f9', color: holdsClients ? '#fff' : '#64748b', border: holdsClients ? 0 : '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                                    {(m.email ?? '?').slice(0, 2).toUpperCase()}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>
                                        {m.email ?? 'Unknown'}
                                        {isMe && <span style={{ marginLeft: 6, fontSize: 10.5, color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: 4, padding: '1px 5px' }}>you</span>}
                                        {m.status === 'invited' && <span style={{ marginLeft: 6, fontSize: 10.5, color: '#d97706', background: '#fdf3e5', borderRadius: 4, padding: '1px 5px' }}>invited</span>}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{roleLine(m, holderLabel)}</div>
                                    {holdsClients && (
                                        <div style={{ fontSize: 12, color: m.plan?.mode === 'none' ? '#b45309' : '#64748b', marginTop: 2 }}>
                                            {planLine(m)}
                                            {m.clientCount > 0 && ` · ${m.clientCount} on their book`}
                                        </div>
                                    )}

                                    {holdsClients && (
                                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                                            {editingNumber === m.userId ? (
                                                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                                                    <input
                                                        style={{ ...input, width: 150, padding: '5px 8px', fontSize: 13 }}
                                                        value={numberDraft}
                                                        onChange={(e) => setNumberDraft(e.target.value)}
                                                        placeholder="ASIC adviser number"
                                                        aria-label="ASIC adviser number"
                                                    />
                                                    <button
                                                        type="button"
                                                        style={ghostBtn}
                                                        disabled={busy}
                                                        onClick={async () => {
                                                            const ok = await run(() => updateFirmMember(tenantId, m.userId, { adviserNumber: numberDraft.trim() || null }));
                                                            if (ok) setEditingNumber(null);
                                                        }}
                                                    >
                                                        Save
                                                    </button>
                                                    <button type="button" style={ghostBtn} onClick={() => setEditingNumber(null)}>
                                                        Cancel
                                                    </button>
                                                </span>
                                            ) : (
                                                <>
                                                    {m.adviserNumber ? `Adviser No. ${m.adviserNumber}` : 'No adviser number recorded'}
                                                    {(isMe || isPrincipal) && (
                                                        <button
                                                            type="button"
                                                            style={{ background: 'none', border: 0, color: '#1a6fd4', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '0 0 0 8px' }}
                                                            onClick={() => {
                                                                setEditingNumber(m.userId);
                                                                setNumberDraft(m.adviserNumber ?? '');
                                                            }}
                                                        >
                                                            {m.adviserNumber ? 'Change' : 'Add'}
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Actions exist only where they mean something. A sole practitioner sees none
                                    of this, because every one of them needs a second person to be possible. */}
                                {isPrincipal && !isMe && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                                        {m.role === 'adviser' && (
                                            <button
                                                type="button"
                                                style={ghostBtn}
                                                disabled={busy}
                                                onClick={async () => {
                                                    if (!(await confirmAction(`Hand the account to ${m.email}? They will see every client and manage the subscription. You cannot undo this yourself.`))) return;
                                                    void run(() => transferOwnership(tenantId, m.userId, 'adviser'));
                                                }}
                                            >
                                                Make principal
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            style={ghostBtn}
                                            disabled={busy}
                                            onClick={async () => {
                                                if (!(await confirmAction(`Remove ${m.email} from the firm?`))) return;
                                                void run(() => removeFirmMember(tenantId, m.userId));
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
            </div>

            {isPrincipal && (
                <div style={{ padding: '14px 20px 20px', borderTop: '1px solid #f1f5f9' }}>
                    {!inviteOpen ? (
                        <button type="button" style={primaryBtn} onClick={() => setInviteOpen(true)}>
                            Invite a colleague
                        </button>
                    ) : (
                        <div>
                            <label style={label} htmlFor="team-invite-email">
                                Their work email
                            </label>
                            <input
                                id="team-invite-email"
                                style={input}
                                type="email"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                placeholder="name@firm.com.au"
                            />

                            <div style={{ marginTop: 14 }}>
                                <span style={label}>What will they do?</span>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                                    {(
                                        [
                                            { value: 'adviser' as const, title: holderLabel, detail: 'Has their own clients. Needs their own plan before taking one on.' },
                                            { value: 'staff' as const, title: 'Support', detail: "Helps with anyone's clients. Holds none of their own. Free." },
                                        ]
                                    ).map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setInviteRole(opt.value)}
                                            style={{
                                                textAlign: 'left',
                                                cursor: 'pointer',
                                                background: inviteRole === opt.value ? '#f0f7ff' : '#fff',
                                                border: inviteRole === opt.value ? '2px solid #1a6fd4' : '1px solid #e2e8f0',
                                                borderRadius: 12,
                                                padding: inviteRole === opt.value ? 12 : 13,
                                            }}
                                        >
                                            <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a', marginBottom: 3 }}>{opt.title}</div>
                                            <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>{opt.detail}</div>
                                        </button>
                                    ))}
                                </div>
                                <p style={{ fontSize: 12, color: '#64748b', margin: '8px 0 0', lineHeight: 1.5 }}>
                                    Every client file names the {holderLabel.toLowerCase()} responsible for it. Support can
                                    prepare a file, but it is always assigned to {holderLabel === 'Adviser' ? 'an adviser' : `a ${holderLabel.toLowerCase()}`}.
                                </p>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9, marginTop: 16 }}>
                                <button type="button" style={ghostBtn} onClick={() => { setInviteOpen(false); setInviteLink(null); }}>
                                    Cancel
                                </button>
                                <button type="button" style={primaryBtn} disabled={busy || !inviteEmail.trim()} onClick={() => void send()}>
                                    Send invite
                                </button>
                            </div>

                            {inviteLink && (
                                <div style={{ marginTop: 14, background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 14px' }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>Send them this link</div>
                                    <p style={{ fontSize: 12, color: '#475569', margin: '0 0 8px', lineHeight: 1.5 }}>
                                        Colleague invites are not emailed automatically — pass this on yourself. It works once,
                                        and only for the address you entered.
                                    </p>
                                    <code style={{ display: 'block', wordBreak: 'break-all', fontSize: 11.5, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', color: '#334155' }}>
                                        {inviteLink}
                                    </code>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
